import React, { useEffect, useMemo, useState } from "react";
import { VictoryChart, VictoryLine, VictoryAxis, VictoryLegend } from "victory";
import { useSO01 } from "../../hooks/BWOS/useSO01";
import styled from "styled-components";
import { toBWOSChartTime } from "../../utils/Converters";
import useMeasure from "react-use-measure";
import { useSO01history } from "../../hooks/BWOS/useSO01history";
import { useOP13 } from "../../hooks/BWOS/useOP13.js";
import { ProgressTubeBar } from "../../interface/ProgressTubeBar.jsx";
import { useOM05OpenPicks } from "../../hooks/BWOS/useOM05OpenPicks.js";
import { reduce } from "d3";

const StyledChart = styled.div`
  position: relative; /* so child overlay can position absolutely */
  width: 100%;
  height: 100%;
  padding: 1rem;
`;

const StyledHeading = styled.h5`
  color: var(--color-grey-500);
  padding-top: 1em;
  padding-left: 2em;
  margin-bottom: -1rem;
`;

const StyledEstimateHeading = styled.h5`
  color: var(--color-witron-blue);
  margin-top: 0.3em;
  padding-left: 5em;
`;

const StyledEstimate = styled.h5`
  color: var(--color-grey-500);
  align-self: center;
  white-space: nowrap;
  font-size: 1.3em;

  padding-left: 0.4em;
`;

const OverlayProgressWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 15%;
  width: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  padding-left: 6rem;
  justify-content: start;
  z-index: 5;
  pointer-events: none;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 30;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #ccc;
  cursor: pointer;
`;

const Drawer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 20;
  height: 100%;
  width: 100px;
  background: rgba(0, 0, 20, 0.8);
  transform: translateX(${(p) => (p.open ? "0" : "-100%")});
  transition: transform 0.3s ease;
  padding: 1rem;
  padding-top: 4rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DrawerItem = styled.button`
  background: none;
  border: none;
  color: #fff;
  text-align: left;
  padding: 0.5rem 0;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

function ComChart() {
  const [ref, bounds] = useMeasure(); //Add on to make the chart adaptive to samller monitors
  const [chartStartTime] = useState(new Date(2000, 0, 1, 4, 0)); // 06:00
  const [chartEndTime] = useState(new Date(2000, 0, 1, 23, 59)); // 18:00
  const [chartType, setChartType] = useState("Line Chart");
  const [drawerOpen, setDrawerOpen] = useState(false);


const [measuredWidth, setMeasuredWidth] = useState(null);

useEffect(() => {
  if (bounds.width > 0 && measuredWidth === null) {
    setMeasuredWidth(bounds.width);
  }
}, [bounds.width, measuredWidth]);



const chartWidth = measuredWidth ?? 800;
const chartHeight = chartWidth / 2.2;


  const {
    data: yesterdayData,
    isLoading: loadingY,
    error: errorY,
  } = useSO01history();
  const { data: todayData, isLoading: loadingT, error: errorT } = useSO01();
  const { data: nowData, isLoading: loadingN, error: errorN } = useOP13();
  const {
    data: prodData,
    isLoading: loadingP,
    error: errorP,
  } = useOM05OpenPicks();

  // 2) transform into a “10-min build + plateau” step series
  const stepData = useMemo(() => {
    if (!Array.isArray(nowData) || nowData.length === 0) return [];

    // 1) ensure chronological order
    const sorted = [...nowData].sort(
      (a, b) =>
        new Date(a.o_TIMESLOT_FROM).getTime() -
        new Date(b.o_TIMESLOT_FROM).getTime()
    );

    const pts = [];
    let currentHour = null;
    let runningSum = 0;

    sorted.forEach((row) => {
      const dt = new Date(row.o_TIMESLOT_FROM);
      const h = dt.getHours();
      const m = dt.getMinutes();
      const cnt = parseInt(row.o_CNTCOMSUM, 10) || 0;

      // 2) when we hit a new hour, reset and emit the 0-point
      if (h !== currentHour) {
        currentHour = h;
        runningSum = 0;
        const startOfHour = new Date(2000, 0, 1, h, 0, 0);
        if (startOfHour >= chartStartTime && startOfHour <= chartEndTime) {
          pts.push({ x: startOfHour, y: 0 });
        }
      }

      // 3) accumulate and emit each two-minute total
      runningSum += cnt;
      const x = new Date(2000, 0, 1, h, m, 0);
      if (x >= chartStartTime && x <= chartEndTime) {
        pts.push({ x, y: runningSum });
      }
    });

    return pts;
  }, [nowData, chartStartTime, chartEndTime]);

  if (loadingY || loadingT || loadingP) return <div>Loading...</div>;
  if (errorY || errorT || errorP) return <div>Error loading chart data</div>;
  if (!yesterdayData?.length && !todayData?.length)
    return <div>No data available</div>;

  const parseData = (rows, startTime, endTime) =>
    rows
      .map((row) => {
        const x = toBWOSChartTime(row.o_TIME_FROM, startTime, endTime);
        const y = parseInt(row.o_CNTCOMPICKSSUM, 10);
        return x && !isNaN(y) ? { x, y } : null;
      })
      .filter(Boolean);

  function smoothCurrentHour(data, windowSize = 5) {
    const currentHour = new Date().getHours();

    const inHour = data
      .filter((row) => new Date(row.o_TIMESLOT_FROM).getHours() === currentHour)
      .map((row) => parseInt(row.o_CNTCOMSUM, 10) * 30)
      .filter((y) => !isNaN(y));

    const smooth =
      inHour.slice(-windowSize).reduce((a, b) => a + b, 0) / windowSize;
    return Math.round(smooth);
  }

  function getNowLine(data) {
    const hourly = bucketNowDataByHour(data);
    const currentHour = new Date().getHours();
    const smooth = smoothCurrentHour(data, 5);

    const result = [];

    for (let hour in hourly) {
      const h = parseInt(hour, 10);
      const x = new Date(2000, 0, 1, h, 0, 0, 0);
      const y = h === currentHour ? smooth : Math.round(hourly[h].sum);

      result.push({ x, y });
    }

    const clamped = result.filter((point) => {
      return point.x >= chartStartTime && point.x <= chartEndTime;
    });
    return clamped.sort((a, b) => a.x - b.x);
  }

  function bucketNowDataByHour(data) {
    const hourly = {};

    data.forEach((point) => {
      const date = new Date(point.o_TIMESLOT_FROM);
      const hour = date.getHours();
      const y = parseInt(point.o_CNTCOMSUM, 10);

      if (!hourly[hour]) hourly[hour] = { sum: 0, count: 0 };
      hourly[hour].sum += isNaN(y) ? 0 : y;
      hourly[hour].count++;
    });

    return hourly;
  }

  prodData && console.log("OM05data", prodData);

  const yesterday = parseData(
    yesterdayData ?? [],
    chartStartTime,
    chartEndTime
  );
  const todayRaw = parseData(todayData ?? [], chartStartTime, chartEndTime);
  const now = getNowLine(nowData ?? []);
  const openUMinCOM =
    prodData?.reduce(
      (acc, cur) => acc + (parseInt(cur.umOpenCOMAmb, 10) || 0),
      0
    ) ?? 0;
  const maxProduction =
    (nowData?.reduce(
      (acc, cur) => acc + (parseInt(cur.o_CNTCOMSUM, 10) || 0),
      0
    ) ?? 0) + openUMinCOM;
  const currProductionPerc =
    ((nowData?.reduce(
      (acc, cur) => acc + (parseInt(cur.o_CNTCOMSUM, 10) || 0),
      0
    ) ?? 0) *
      100) /
    maxProduction;
  const currentRestProduction = (openUMinCOM * 100) / maxProduction;
  const restLabel =
    String(openUMinCOM) + " (" + String(Math.round(currProductionPerc)) + "%)";

  // 30 rows = 30 * 2min = 60min = 1 hour
  const recentRows = nowData?.slice(-30) ?? [];
  const recentSum = recentRows.reduce(
    (acc, cur) => acc + (parseInt(cur.o_CNTCOMSUM, 10) || 0),
    0
  );
  const unitsPerHour = recentSum; // Already based on 1 hour

  // Estimate remaining time
  const hoursLeft = unitsPerHour > 0 ? openUMinCOM / unitsPerHour : 0;
  const wholeHours = Math.floor(hoursLeft);
  const minutesLeft = Math.round((hoursLeft - wholeHours) * 60);
  const timeNow = new Date();
  const completionDate = new Date(
    timeNow.getTime() + hoursLeft * 60 * 60 * 1000
  );

  const pad = (num) => String(num).padStart(2, "0");
  const estimatedTimeLabel = `${pad(completionDate.getHours())}:${pad(
    completionDate.getMinutes()
  )}`;

  // Label string
  // const estimatedTimeLabel =
  //   unitsPerHour === 0
  //     ? "∞"
  //     : `${wholeHours > 0 ? `${wholeHours}h ` : ""}${minutesLeft}m left`;

  return (
    <StyledChart ref={ref}>
      {/* 1) Hamburger toggle */}
      <ToggleButton onClick={() => setDrawerOpen((o) => !o)}>☰</ToggleButton>

      {/* 2) Side-drawer menu */}
      <Drawer open={drawerOpen}>
        <DrawerItem
          onClick={() => {
            setChartType("Line Chart");
            setDrawerOpen(false);
          }}
        >
          Line Chart
        </DrawerItem>
        <DrawerItem
          onClick={() => {
            setChartType("Step Chart");
            setDrawerOpen(false);
          }}
        >
          Step Chart
        </DrawerItem>
        {/* add more menu items here later */}
      </Drawer>
      <StyledHeading>COM Picks</StyledHeading>
      <VictoryChart
        width={chartWidth}
        height={chartHeight}
        domainPadding={5}
        scale={{ x: "time" }}
      >
        <VictoryAxis
          tickFormat={(t) => `${t.getHours()}:00`}
          fixLabelOverlap
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />
        <VictoryAxis
          dependentAxis
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />

        {chartType === "Step Chart" && (
          <VictoryLine
            data={stepData}
            interpolation="stepAfter"
            style={{ data: { stroke: "orange", strokeWidth: 2 } }}
          />
        )}

        <VictoryLine
          data={yesterday}
          interpolation="monotoneX"
          style={{ data: { stroke: "steelblue" } }}
        />

        {chartType === "Line Chart" && (
          <VictoryLine
            data={todayRaw}
            interpolation="monotoneX"
            style={{ data: { stroke: "black" } }}
          />
        )}
        {chartType === "Line Chart" && (
          <VictoryLine
            data={now}
            interpolation="monotoneX"
            style={{ data: { stroke: "orange", strokeWidth: 1.5 } }}
          />
        )}

        <VictoryLegend
          orientation="horizontal"
          gutter={20}
          x={40}
          style={{ labels: { fill: "#ccc", fontSize: 12 } }}
          itemsPerRow={3}
          data={[
            { name: "Yesterday", symbol: { fill: "steelblue" } },
            {
              name: chartType === "Step Chart" ? "Step" : "Today",
              symbol: {
                fill: chartType === "Step Chart" ? "steelblue" : "orange",
              },
            },
          ]}
        />
      </VictoryChart>
      <OverlayProgressWrapper>
        <ProgressTubeBar
          value={currentRestProduction}
          width={bounds.width * 0.5}
          label={restLabel}
          minLabel="0"
          maxLabel={String(maxProduction)}
          thresholds={{ yellow: 100, red: 100 }}
          color="var(--color-witron-blue)"
        />
        <StyledEstimateHeading>ESTIMATED FINISHED:</StyledEstimateHeading>
        <StyledEstimate>{estimatedTimeLabel}</StyledEstimate>
      </OverlayProgressWrapper>
    </StyledChart>
  );
}

export default ComChart;
